import { createSlice } from "@reduxjs/toolkit";
import { resolveNegStatus } from "../lib/negotiationNegStatus";
import { mergeNotesArray, shouldApplyIncomingRecord, syncCaseAttorneyFromBatchPayload } from "../lib/caseUpdateHelpers";
import { isDeletedProcoDocument } from "../lib/docs";

function syncActiveCaseNegStatus(state) {
    if (!state.activeCase?.case_negotiation) {
        return;
    }
    state.activeCase.case_negotiation.neg_status = resolveNegStatus(
        state.activeCase.case_negotiation,
        state.activeCase.practices
    );
}

function mergeArrayById(target, incoming) {
    if (!Array.isArray(target)) return incoming
    if (!Array.isArray(incoming)) return target

    const allItemsHaveId = incoming.every(
        (item) => item && typeof item === "object" && "id" in item
    )

    if (!allItemsHaveId) {
        return incoming
    }

    incoming.forEach((item) => {
        const index = target.findIndex((existing) => existing?.id == item.id)
        if (index !== -1) {
            if (item.force_remove) {
                target.splice(index, 1)
            } else if (shouldApplyIncomingRecord(target[index], item)) {
                target[index] = {
                    ...target[index],
                    ...item,
                }
            }
        } else if (!item.force_remove) {
            target.unshift(item)
        }
    })

    return target
}

function mergeNestedObject(target, incoming) {
    if (!target || typeof target !== "object" || !incoming || typeof incoming !== "object") {
        return
    }

    Object.entries(incoming).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            if (key === "notes") {
                target[key] = mergeNotesArray(target[key], value)
                return
            }
            if (!Array.isArray(target[key])) {
                target[key] = []
            }
            target[key] = mergeArrayById(target[key], value)
            return
        }

        if (value && typeof value === "object") {
            if (!target[key] || typeof target[key] !== "object" || Array.isArray(target[key])) {
                target[key] = {}
            }
            mergeNestedObject(target[key], value)
            return
        }

        target[key] = value
    })
}

function upsertFrontById(target, incomingItem) {
    if (!Array.isArray(target) || !incomingItem || typeof incomingItem !== "object") {
        return
    }
    if (!("id" in incomingItem)) {
        target.unshift(incomingItem)
        return
    }

    const index = target.findIndex((existing) => existing?.id == incomingItem.id)
    if (index !== -1) {
        target[index] = {
            ...target[index],
            ...incomingItem,
        }
    } else {
        target.unshift(incomingItem)
    }
}

const initialCaseState = {
    activeCase: null,
    caseHistory: null,
    lastactiveCases: [],
    currentRequests: null,
}

const caseSlice = createSlice({
    name: 'patient',
    initialState: initialCaseState,
    reducers: {
        setActiveCase(state, data) {
            state.activeCase = data.payload
        },
        setCaseHistory(state, data) {
            state.caseHistory = data.payload
        },
        modifyActiveCase(state, data) {
            let info = data.payload
            // property to change is dependend with other items
            if (info.groupRelation) {
                state.activeCase[info.folder].forEach(element => {
                    if (element.id !== info.item.itemId) {
                        element[info.item.field] = info.groupRelation.value
                    }
                })
            }

            if (info.specificRelation) {
                state.activeCase[info.folder].forEach(element => {
                    if (element.id == info.specificRelation.specificItemId) {
                        element[info.item.field] = info.specificRelation.value
                    }
                })
            }

            // main functionality to change property
            state.activeCase[info.folder].forEach(element => {
                if (element.id == info.item.itemId) {
                    element[info.item.field] = info.item.value

                    // same item different but related property
                    if (info.item.relatedField) {
                        element[info.item.relatedField] = info.item.relatedFieldValue
                    }
                }
            });
        },
        modifyActiveCaseMultiple(state, data) {
            let info = data.payload
            if (info.folder && info.folder != 'worker_comp') {
                state.activeCase[info.folder].forEach(element => {
                    if (element.id == info.item.itemId) {
                        info.item.fields.forEach((el) => {
                            element[el.field] = el.value
                        })
                        // element[info.item.field] = info.item.value
                        
                    }
                });
            } else {
                info.item.fields.forEach((el) => {
                    state.activeCase[info.folder][el.field] = el.value
                })
            }
            if (info.folder === 'practices' || info.folder === 'case_negotiation') {
                syncActiveCaseNegStatus(state);
            }
            // main functionality to change property
            // state.activeCase[info.folder].forEach(element => {
            //     if (element.id == info.item.itemId) {
            //         info.item.fields.forEach((el) => {
            //             element[el.field] = el.value
            //         })
            //         // element[info.item.field] = info.item.value
                    
            //     }
            // });
        },
        modifyActiveCaseMultipleObject(state, data) {
            let info = data.payload

            if (!state.activeCase[info.folder]) {
                state.activeCase[info.folder] = {};
            }

            info.item.fields.forEach((el) => {
                if (el.field === 'escalate_negotiation') {
                    state.activeCase[info.folder][el.field] =
                        el.value === true || el.value === 1 || el.value === '1' ? 1 : 0;
                } else {
                    state.activeCase[info.folder][el.field] = el.value;
                }
            });
            if (info.folder === 'case_negotiation') {
                syncActiveCaseNegStatus(state);
            }
        },
        insertAttorneyInActiveCase(state, data) {
            if (state.activeCase) {
                upsertFrontById(state.activeCase.attorneys, data.payload)
            }
        },
        insertLiabilityInActiveCase(state, data) {
            if (state.activeCase) {
                upsertFrontById(state.activeCase.liabilities, data.payload)
            }
        },
        insertMedPayUMInActiveCase(state, data) {
            if (state.activeCase) {
                upsertFrontById(state.activeCase.mp_pip_um, data.payload)
            }
        },
        insertMajorMedInActiveCase(state, data) {
            if (state.activeCase) {
                upsertFrontById(state.activeCase.major_meds, data.payload)
            }
        },
        setInitialDataInArray(state, data) {
            let info = data.payload

            if (state.activeCase[info.folder]) {
                state.activeCase[info.folder].push(info.item)
            }
        },
        setInitialDataInArrayFirstPosition(state, data) {
            let info = data.payload

            if (state.activeCase[info.folder]) {
                state.activeCase[info.folder].unshift(info.item)
            }
        },
        pushItemToDDocuments(state, data) {
            if (state.activeCase.documents) {
                state.activeCase.documents.unshift(data.payload)
            } else {
                state.activeCase.documents = [data.payload]
            }
        },
        replaceWholeItemInDocuments(state, data) {
            if (state.activeCase.documents) {
                const index = state.activeCase.documents.findIndex(object => {
                    return object.id == data.payload.id                  
                })

                if (index !== -1) { //we have a match - update the item!
                    state.activeCase.documents[index] = data.payload
                } 
            }
        },
        replaceItemInDocuments(state, data) {
            
            if (state.activeCase.documents) {
                const index = state.activeCase.documents.findIndex(object => {
                    return data.payload.versions.some((item) => {
                        return object.id == item.id
                    })                    
                })

                state.activeCase.documents[index] = data.payload
            }
        },
        // setManagingAttorneyHippaLOPDoc(state, data) {
        //     if (state.activeCase.attorneys && state.activeCase.attorneys.length > 0) {
        //         state.activeCase.attorneys.forEach(attorney => {
        //             if (attorney.is_managing_attorney) {
        //                 if (attorney.case_doc_type_options && attorney.case_doc_type_options.practices && attorney.case_doc_type_options.practices.length> 0) {
                            
        //                     data.payload.forEach(elem => {
        //                         const index = attorney.case_doc_type_options.practices.findIndex(object => {
        //                             return object.practice_id == elem.practice_id                  
        //                         })

        //                         if (index !== -1) { //index found!!!!!!!!!!
                                    
        //                             if (elem.has_hippa) {
        //                                 attorney.case_doc_type_options.practices[index].has_hippa = true
        //                             }
        //                             if (elem.has_lop) {
        //                                 attorney.case_doc_type_options.practices[index].has_lop = true
        //                             }
        //                         } else { //index not found, just put the element there  
        //                             attorney.case_doc_type_options.practices.push(elem)
        //                         }
        //                     })
        //                 } else {
        //                     attorney.case_doc_type_options.practices = data.payload
        //                 }
        //             }
        //         })
        //     }
        // },
        removeFromArray(state, data) {
            if (state.activeCase[data.payload.folder] && state.activeCase[data.payload.folder].length> 0) {
                const index = state.activeCase[data.payload.folder].findIndex(object => {
                    return object.id == data.payload.itemId                  
                })
                if (index !== -1) {
                    state.activeCase[data.payload.folder].splice(index, 1)
                }
            }
        },
        replaceFolder(state, data) {
            state.activeCase[data.payload.folder] = data.payload.arr
        },
        pushItemInObjArray(state, data) {
            if (state.activeCase[data.payload.folder] && state.activeCase[data.payload.folder][data.payload.label]) {
                state.activeCase[data.payload.folder][data.payload.label].unshift(data.payload.item)
            }
        },
        pushWholeItemInArray(state, data) {
            if (state.activeCase[data.payload.folder]) {
                const index = state.activeCase[data.payload.folder].findIndex(object => {
                    return object.id == data.payload.itemId                  
                })
                if (index !== -1) {                
                    state.activeCase[data.payload.folder][index] = data.payload.item
                } else {
                    state.activeCase[data.payload.folder].push(data.payload.item)
                }
            }
        },
        changetemInObjArray(state, data) {
            if (state.activeCase[data.payload.folder] && state.activeCase[data.payload.folder][data.payload.label]) {
                const index = state.activeCase[data.payload.folder][data.payload.label].findIndex(object => {
                    return object.id == data.payload.item.id                  
                })
                if (index !== -1) {
                    // state.activeCase[data.payload.folder][data.payload.label].splice(index, 1)
                    // state.activeCase[data.payload.folder][data.payload.label].push(data.payload.item)
                    state.activeCase[data.payload.folder][data.payload.label][index] = data.payload.item
                }
            }
        },
        changeORAddItemInObjArray(state, data) {
            if (state.activeCase[data.payload.folder] && state.activeCase[data.payload.folder][data.payload.label]) {
                const index = state.activeCase[data.payload.folder][data.payload.label].findIndex(object => {
                    return object.id == data.payload.item.id                  
                })
                if (index !== -1) {                
                    state.activeCase[data.payload.folder][data.payload.label][index] = data.payload.item
                } else {
                    state.activeCase[data.payload.folder][data.payload.label].push(data.payload.item)
                }
            }
        },
        pushLastActiveCases(state, data) {
            // const index = state.lastactiveCases.indexOf(data.payload)
            const index = state.lastactiveCases.findIndex(object => {
                return object.id == data.payload.id                  
            })

            if (index == -1) {
                state.lastactiveCases.unshift(data.payload)
            } else {
                if (index === 0) {
                    state.lastactiveCases[0] = data.payload
                } else {
                    state.lastactiveCases.splice(index, 1)
                    state.lastactiveCases.unshift(data.payload)
                }
            }
            
        },
        setDepartmentOnMajorMed(state, data) {
            const itemIndex = state.activeCase.major_meds.findIndex(object => {
                return object.id == data.payload.itemId                  
            })

            if (itemIndex !== -1) { // major med item found
                let departmentIndex = state.activeCase.major_meds[itemIndex].major_med_departments.findIndex(object => {
                    return object.department_id == data.payload.departmentId                  
                })

                if (departmentIndex !== -1) { //department found
                    state.activeCase.major_meds[itemIndex].major_med_departments[departmentIndex] = data.payload.data
                } else {
                    state.activeCase.major_meds[itemIndex].major_med_departments.push(data.payload.data)
                }

            }
        },
        setServiceOnMajorMed(state, data) {
           
            const itemIndex = state.activeCase.major_meds.findIndex(object => {
                return object.id == data.payload.itemId                  
            })

            if (itemIndex !== -1) { 
                let departmentIndex = state.activeCase.major_meds[itemIndex].major_med_departments.findIndex(object => {
                    return object.department_id == data.payload.departmentId                  
                })

                if (departmentIndex !== -1) {// what if department is not created yet??????
                    let serviceIndex = state.activeCase.major_meds[itemIndex].major_med_departments[departmentIndex].services.findIndex(object => {
                        return object.service_id == data.payload.serviceId                  
                    })

                    if (serviceIndex !== -1) { //there is a service on the department, just replace it
                        state.activeCase.major_meds[itemIndex].major_med_departments[departmentIndex].services[serviceIndex] = data.payload.data
                    } else {  //there is NOT! a service on the department, need to create one
                        state.activeCase.major_meds[itemIndex].major_med_departments[departmentIndex].services.push(data.payload.data)
                    }
                }
            }         
            
        },
        setSurgicalOnMajorMed(state, data) {
           
            const itemIndex = state.activeCase.major_meds.findIndex(object => {
                return object.id == data.payload.itemId                  
            })

            if (itemIndex !== -1) { 
                let departmentIndex = state.activeCase.major_meds[itemIndex].major_med_departments.findIndex(object => {
                    return object.department_id == data.payload.departmentId                  
                })

                if (departmentIndex !== -1) {
                    let surgicalIndex = state.activeCase.major_meds[itemIndex].major_med_departments[departmentIndex].surgeries.findIndex(object => {
                        return object.surgery_id == data.payload.surgicalId                  
                    })

                    if (surgicalIndex !== -1) { //there is a service on the department, just replace it
                        state.activeCase.major_meds[itemIndex].major_med_departments[departmentIndex].surgeries[surgicalIndex] = data.payload.data
                    } else {  //there is NOT! a service on the department, need to create one
                        state.activeCase.major_meds[itemIndex].major_med_departments[departmentIndex].surgeries.push(data.payload.data)
                    }
                }
            }         
            
        },
        setDepartmentOnWorkersComp(state, data) {
            let departmentIndex = state.activeCase.worker_comp.worker_comp_departments.findIndex(object => {
                return object.department_id == data.payload.departmentId                  
            })

            if (departmentIndex !== -1) { //department found
                state.activeCase.worker_comp.worker_comp_departments[departmentIndex] = data.payload.data
            } else {
                state.activeCase.worker_comp.worker_comp_departments.push(data.payload.data)
            }
        },
        setServiceOnWorkersComp(state, data) {
            
            let departmentIndex = state.activeCase.worker_comp.worker_comp_departments.findIndex(object => {
                return object.department_id == data.payload.departmentId                  
            })

            if (departmentIndex !== -1) {// what if department is not created yet??????
                let serviceIndex = state.activeCase.worker_comp.worker_comp_departments[departmentIndex].services.findIndex(object => {
                    return object.service_id == data.payload.serviceId                  
                })

                if (serviceIndex !== -1) { //there is a service on the department, just replace it
                    state.activeCase.worker_comp.worker_comp_departments[departmentIndex].services[serviceIndex] = data.payload.data
                } else {  //there is NOT! a service on the department, need to create one
                    state.activeCase.worker_comp.worker_comp_departments[departmentIndex].services.push(data.payload.data)
                }
            }        
        },
        setSurgicalOnWorkersComp(state, data) {
           
            let departmentIndex = state.activeCase.worker_comp.worker_comp_departments.findIndex(object => {
                return object.department_id == data.payload.departmentId                  
            })

            if (departmentIndex !== -1) {
                let surgicalIndex = state.activeCase.worker_comp.worker_comp_departments[departmentIndex].surgeries.findIndex(object => {
                    return object.surgery_id == data.payload.surgicalId                  
                })

                if (surgicalIndex !== -1) { //there is a service on the department, just replace it
                    state.activeCase.worker_comp.worker_comp_departments[departmentIndex].surgeries[surgicalIndex] = data.payload.data
                } else {  //there is NOT! a service on the department, need to create one
                    state.activeCase.worker_comp.worker_comp_departments[departmentIndex].surgeries.push(data.payload.data)
                }
            }        
            
        },
        replacePatientData(state, data) {
            state.activeCase.patient = data.payload
        },
        updateSpecialityData(state, data) {
            state.activeCase.aica_department_status = data.payload
        },

        updateAddLien(state, data) {

            const practiceIndex = state.activeCase.practices.findIndex(object => {
                return object.id == data.payload.case_practice_id                  
            })

            if (practiceIndex !== -1) {
                if (state.activeCase.practices[practiceIndex].liens.length > 0) {
                    const liensIndex = state.activeCase.practices[practiceIndex].liens.findIndex(object => {
                        return object.type == data.payload.content.type                  
                    })

                    if (liensIndex !== -1) { // there is a liens with the same type, replace it!
                        state.activeCase.practices[practiceIndex].liens[liensIndex] = data.payload.content
                    } else { // no liens found for that type, so push it!
                        state.activeCase.practices[practiceIndex].liens.push(data.payload.content)
                    }


                } else { //there is no liens, just push the content!
                    state.activeCase.practices[practiceIndex].liens.push(data.payload.content)
                }
            }
            
        },
        updateLienProperty(state, data) {
            const practiceIndex = state.activeCase.practices.findIndex(object => {
                return object.id == data.payload.case_practice_id                  
            })

            if (practiceIndex !== -1) {
                const lienIndex = state.activeCase.practices[practiceIndex].liens.findIndex(object => {
                    return object.id == data.payload.id                  
                })

                if (lienIndex !== -1) {
                    state.activeCase.practices[practiceIndex].liens[lienIndex][data.payload.property] = data.payload.value
                }
            }
        },
        setAllRepsOnItem(state, data) {
            // search the attorney 

            if (data.payload && data.payload.folder != 'worker_comp') {
                const index = state.activeCase[data.payload.folder].findIndex(object => {
                    return object.id == data.payload.item_id                  
                })
    
                if (index !== -1) { // we have a match, do the thing!
                    state.activeCase[data.payload.folder][index].all_reps = data.payload.content
                }
            } else {
                state.activeCase[data.payload.folder].all_reps = data.payload.content
            }
            
        },
        replaceWholeFolder(state, data) {
            state.activeCase[data.payload.folder] = data.payload.content
        },
        createOrUpdatePayment(state, data) {
            const index = state.activeCase.case_negotiation.payments.findIndex(object => {
                return object.id == data.payload.id                  
            })

            if (index !== -1) { //we have a match - update the item!
                state.activeCase.case_negotiation.payments[index] = data.payload
            } else { // this is a new payment
                state.activeCase.case_negotiation.payments.push(data.payload)
            }
            
        },
        batchUpdate(state, data) {
            if (!data.payload || !state.activeCase) {
                return
            }
                for (const [key, value] of Object.entries(data.payload)) {

                    if (key === "notes" && Array.isArray(value)) {
                        state.activeCase.notes = mergeNotesArray(state.activeCase.notes, value)
                    } else if (Array.isArray(value)) {
                        if (!Array.isArray(state.activeCase[key])) {
                            state.activeCase[key] = []
                        }
                        mergeArrayById(state.activeCase[key], value)
                    } else if (value && typeof value === "object") {
                        if (!state.activeCase[key] || typeof state.activeCase[key] !== "object" || Array.isArray(state.activeCase[key])) {
                            state.activeCase[key] = {}
                        }
                        mergeNestedObject(state.activeCase[key], value)
                    } else {
                        state.activeCase[key] = value
                    }
                }
                syncCaseAttorneyFromBatchPayload(state.activeCase, data.payload)
                syncActiveCaseNegStatus(state);
            
        },
        markAllMessagesAsRead(state) {
            if (state.activeCase.case_procodocs_chats) {
                state.activeCase.case_procodocs_chats = state.activeCase.case_procodocs_chats.map(item => {
                    return {
                        ...item,
                        is_read: 1
                    }
                })
            }
        },
        pushItemToProcoDocuments(state, data) {
            if (state.activeCase.procodocs_documents) {
                state.activeCase.procodocs_documents.unshift(data.payload)
            } else {
                state.activeCase.procodocs_documents = [data.payload]
            }
        },
        replaceItemInProcoDocuments(state, data) {
            if (state.activeCase.procodocs_documents) {
                const index = state.activeCase.procodocs_documents.findIndex(object => {
                    return object.id == data.payload.id                  
                })

                if (index !== -1) { //we have a match - update the item!
                    state.activeCase.procodocs_documents[index] = data.payload
                } 
            }
        },
        replaceDocumentInPractices(state, data) {
            const practiceIndex = state.activeCase.practices.findIndex(object => {
                return object.id == data.payload.case_practice_id                  
            })
            if (practiceIndex != -1) {
                state.activeCase.practices[practiceIndex].mrq_upload = data.payload.newDoc
            }
        },
        updateItemInProcoDocuments(state, data) {
            if (state.activeCase.procodocs_documents) {
                const index = state.activeCase.procodocs_documents.findIndex(object => {
                    return data.payload.versions.some((item) => {
                        return object.id == item.id
                    })                    
                })

                state.activeCase.procodocs_documents[index] = data.payload
            }
        },
        updateTransfersOnPractices(state, data) {
            if (state.activeCase.practices) {
                const practiceIndex = state.activeCase.practices.findIndex(object => {
                    return object.id == data.payload.case_practice_id                  
                })

                if (practiceIndex != -1) {
                    

                    const transferIndex = state.activeCase.practices[practiceIndex].case_practice_transfers.findIndex(object => {
                        return object.id == data.payload.item.id                 
                    })

                    if (transferIndex != -1) {    
                        state.activeCase.practices[practiceIndex].case_practice_transfers[transferIndex] = data.payload.item
                    } else {
                        state.activeCase.practices[practiceIndex].case_practice_transfers.push(data.payload.item)
                    }
                }
            }
        },
        markDocumentAsViewed(state, data) {
            // Viewed status only applies to Law Firm uploads, not ProCo source docs
            if (data.payload.source && data.payload.source !== 'Law Firm') {
                return
            }

            if (state.activeCase.procodocs_documents) {
                const index = state.activeCase.procodocs_documents.findIndex(object => {
                    return object.id == data.payload.id                  
                })
                if (index !== -1) {
                    state.activeCase.procodocs_documents[index].is_viewed = true
                } else {
                    state.activeCase.procodocs_documents.forEach(item => {
                        if (item.versions && item.versions.length > 0) {
                            item.versions.forEach(version => {
                                if (version.id == data.payload.id) {
                                    version.is_viewed = true
                                }
                            })
                        }
                    })
                }

                // ECM alerts only care about unviewed law-firm uploads for this attorney —
                // Proco's own uploads must not keep the red file icon on.
                const attorneyId = data.payload.case_attorney_id
                const hasUnviewedLawFirmDocs = state.activeCase.procodocs_documents
                    .filter((document) => !isDeletedProcoDocument(document))
                    .filter((document) => document.case_attorney_id == attorneyId)
                    .filter((document) => document.source === 'Law Firm')
                    .some((document) => !document.is_viewed)

                if (!hasUnviewedLawFirmDocs) {
                    let attorneyIndex = state.activeCase.attorneys.findIndex(object => {
                        return object.id == attorneyId                  
                    })

                    if (attorneyIndex !== -1) {
                        state.activeCase.attorneys[attorneyIndex].ecm_unread_documents = 0
                    }
                }

            }
        },
        setCurrentRequests(state, data) {
            state.currentRequests = data.payload
        },
        markRequestAsRead(state, data) {
            if (state.currentRequests) {
                state.currentRequests = state.currentRequests.map(request => {
                    if (request.id === data.payload) {
                        return {
                            ...request,
                            unread_messages_count: 0
                        }
                    }
                    return request
                })

                // let allMessagesRead = state.currentRequests.every(request => {
                //     return request.unread_messages_count == 0
                // })
                // if (allMessagesRead) {
                //     let index = state.activeCase.attorneys.findIndex(object => {
                //         return object.id == data.payload.case_attorney_id                  
                //     })
    
                //     if (index !== -1) {
                //         state.activeCase.attorneys[index].ecm_unread_messages = 0
                //     }
                // }
            }
        },
        markRequestMessagesAsReadInAttorney(state, data) {
            let allMessagesRead = state.currentRequests.every(request => {
                return request.unread_messages_count == 0
            })
            if (allMessagesRead) {
                let index = state.activeCase.attorneys.findIndex(object => {
                    return object.id == data.payload.case_attorney_id                  
                })
                if (index !== -1) {
                    state.activeCase.attorneys[index].ecm_unread_messages = 0
                }
            }
            
            
        },
        addVerification(state, data) {
            let index = state.activeCase[data.payload.folder].findIndex(object => {
                return object.id == data.payload.itemId                  
            })
            if (index !== -1) {
                if (state.activeCase[data.payload.folder][index][data.payload.field]) {
                    state.activeCase[data.payload.folder][index][data.payload.field].unshift(data.payload.item)
                } else {
                    state.activeCase[data.payload.folder][index][data.payload.field] = [data.payload.item]
                }
            } 
        },
    }
})

export const caseActions = caseSlice.actions

export default caseSlice.reducer